import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import data from './goldmedalistsinfo.json';
import './dataviz.scss';
import Tooltip from './Tooltip';
import ImageScroll from './ImagesScroll';

const CareerType = () => {
    
    const svgRef = useRef();
    const containerRef = useRef();

    const [tooltipContent, setTooltipContent] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [hovered, setHovered] = useState(null);

    useEffect(() => {
        drawChart();

        const handleResize = debounce(() => {
            drawChart();
        }, 100); // Adjust the debounce time as needed

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []); 
    
    // Debounce function to limit how often drawChart is called
    const debounce = (func, delay) => {
        let timeout;
        return (...args) => {
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => {
                func(...args);
            }, delay);
        };
    };
    

    const sports = Array.from(new Set(data.map(d => d.sportcn)));

    const careerTypes = ["从政", "非体育类的从政", "高校任职", "教练", "体制内其他", "创业/经商", "演艺", "体制外其他", "回归家庭"];

    const careerCountData = sports.map(sportcn => {
        const sportData = data.filter(d => d.sportcn === sportcn);
        return {
          sportcn,
          "从政": sportData.filter(d => d.postRetirement === "从政").length,
          "非体育类的从政": sportData.filter(d => d.postRetirement === "非体育类的从政").length,
          "高校任职": sportData.filter(d => d.postRetirement === "高校任职").length,
          "教练": sportData.filter(d => d.postRetirement === "教练").length,
          "体制内其他": sportData.filter(d => d.postRetirement === "体制内其他").length,
          "创业/经商": sportData.filter(d => d.postRetirement === "创业/经商").length,
          "演艺": sportData.filter(d => d.postRetirement === "演艺").length,
          "体制外其他": sportData.filter(d => d.postRetirement === "体制外其他").length,
          "回归家庭": sportData.filter(d => d.postRetirement === "回归家庭").length,
        //   "不详": sportData.filter(d => d.postRetirement === "不详").length
        };
      });

      const athleteCountperSport = careerCountData.map((d) => {
        return {
          ...d,
          total: Object.values(d).reduce((acc, val) => {
            if (typeof val === "number") return acc + val;
            return acc;
          }, 0),
        };
      });

      athleteCountperSport.sort((a, b) => a.total - b.total);
      
      const color = d3.scaleOrdinal()
        .domain(careerTypes)
        .range([
            "#ED6E79", // 从政
            "#FE89B5", // 非体育类的从政
            "#DA96DF", // 高校任职
            "#F5A26E", // 教练
            "#FFDC8A", // 体制内其他
            "#658DE5", // 创业/经商 (外)
            "#898CFF", // 演艺
            "#59D0E4", // 体制外其他
            "#90D4F7", // 回归家庭
            ]);

    //legend
    const legendTooltip = {
        "非体育类的从政": "<div class='tooltip__details'>职务包括残联副理事长、党组副书记等</div>",
        "体制内其他": "<div class='tooltip__details'>体操冠军李珊珊进入中国银行澳门分行</div>",
        "体制外其他": "<div class='tooltip__details'>包括社会公益、作家、自媒体、疗养院工作等</div>",
    };


    const drawChart = () => {
        // const margin = { top: 20, right: 30, bottom: 40, left: 60 };
        // const width = 960 - margin.left - margin.right;
        // const height = 800 - margin.top - margin.bottom;

        // const svg = d3.select(svgRef.current)
        // .attr('width', width + margin.left + margin.right)
        // .attr('height', height + margin.top + margin.bottom)
        // .append('g')
        // .attr('transform', `translate(${margin.left},${margin.top})`);

        const container = d3.select(containerRef.current);
        const svg = d3.select(svgRef.current);
        const containerWidth = container.node().clientWidth;
        const containerHeight = container.node().clientHeight;

        const margin = { top: 40, right: 10, bottom: 40, left: 10 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;

        svg
        .attr('viewBox', `0 0 ${containerWidth} ${containerHeight}`)
        .attr('preserveAspectRatio', 'xMinYMin meet')
        .attr('width', '100%')
        .attr('height', '100%');


        const svgGroup = svg
        .selectAll('g')
        .data([null])
        .join('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);


        const filteredCareerData = athleteCountperSport.filter(d => d.sportcn !== '单板滑雪');

        const y = d3.scaleBand()
        .domain(filteredCareerData.map(d => d.sportcn))
        .range([height, 0])
        .paddingInner(0.45)
        .paddingOuter(0.1);


        //体制外
        const stackRight = d3.stack()
        .keys(["创业/经商", "演艺", "体制外其他", "回归家庭"])
        .order(d3.stackOrderNone)
        .offset(d3.stackOffsetNone);

        //体制内
        const stackLeft = d3.stack()
        .keys(["从政", "非体育类的从政", "高校任职", "教练", "体制内其他"])
        .order(d3.stackOrderNone)
        .offset(d3.stackOffsetNone);

        const seriesRight = stackRight(careerCountData);
        const seriesLeft = stackLeft(careerCountData);

        const seriesCount = (data) => {
            const countLeft = data["从政"] + data["非体育类的从政"] + data["高校任职"] + data["教练"] + data["体制内其他"];
            const countRight = data["创业/经商"] + data["演艺"] + data["体制外其他"] + data["回归家庭"];
            return { countLeft, countRight };
            
        };

        const xRight = d3.scaleLinear()
            .domain([0, 20])
            .range([width / 2 + 40, width]);

        const xAxisRight = d3.axisBottom(xRight)
            .ticks(5)
            .tickSize(0);

        const xLeft = d3.scaleLinear()
            .domain([0, 20])
            .range([width / 2 - 40, 0]);

        const xAxisLeft = d3.axisBottom(xLeft)
            .ticks(5)
            .tickSize(0);




//row highlight
        svgGroup.selectAll(".row-highlight")
            .data(filteredCareerData)
            .enter()
            .append("rect")
            .attr("class", "row-highlight")
            .attr("x", 0)
            .attr("y", (d) => y(d.sportcn) - 5)
            .attr("width", width)
            .attr("height", y.bandwidth() + 10)
            .attr("fill", "white")
            .attr("opacity", 0)
            // .lower()
            ;

        


//體制外
        svgGroup.selectAll(".serie-right")
        .data(seriesRight)
        .enter().append("g")
        .attr("fill", d => color(d.key))
        .selectAll("rect")
        // .data(d => d)
        .data(d => {
            return d.map(v => ({ ...v, key: d.key }));
        })
        .enter().append("rect")
        .attr("y", d => y(d.data.sportcn))
        .attr("x", d => xRight(d[0]))
        .attr("height", y.bandwidth())
        .attr("width", d => xRight(d[1]) - xRight(d[0]))
        .on("mouseover", (event, d) => {
                const sport = d.data.sportcn;
                const careerType = d.key;
                const count = d.data[careerType];
                setTooltipContent(`<div class="tooltip__content">
                <span class="tooltip__details"><b>${d.key}</b></span> 
                <span class="tooltip__details">&nbsp;
                 ${count}位</></span></div>`);
                setTooltipPosition({ x: event.pageX, y: event.pageY })
                setHovered(event.target);

                svg.selectAll(".row-highlight")
                .filter((row) => row.sportcn === sport)
                .attr('opacity', 0.8);
            })
        .on("mouseout", () => {
            setTooltipContent(null);
            setHovered(null);
            svg.selectAll(".row-highlight")
            .attr("opacity", 0);
        });

//體制內
        svgGroup.selectAll(".serie-left")
        .data(seriesLeft)
        .enter().append("g")
        .attr("class", "serie-left")
        .attr("fill", d => color(d.key))
        .selectAll("rect")
        .data(d => {
            return d.map(v => ({ ...v, key: d.key }));
        })
        .enter().append("rect")
        .attr("y", d => y(d.data.sportcn))
        .attr("x", d => xLeft(d[1])) // Start from the left
        .attr("height", y.bandwidth())
        .attr("width", d => xLeft(d[0]) - xLeft(d[1])) // Width is the difference between start and end
        .on("mouseover", (event, d) => {
            const sport = d.data.sportcn;
            const careerType = d.key;
            const count = d.data[careerType];
            setTooltipContent(`
                <div class="tooltip__content">
                <span class="tooltip__details"><b>${d.key}</b></span> 
                <span class="tooltip__details">&nbsp;
                 ${count}位</></span></div>
                `);
            setTooltipPosition({ x: event.pageX - 0, y: event.pageY - 0 });
            setHovered(event.target);
            
            svg.selectAll(".row-highlight")
                .filter((row) => row.sportcn === sport)
                .attr('opacity', 0.8);
        })
    .on("mouseout", () => {
        setTooltipContent(null);
        setHovered(null);
        svg.selectAll(".row-highlight")
            .attr("opacity", 0);
    });

        
        // Add y-axis for sports
        svgGroup.append("g")
            .attr("class", "y-axis")
            .attr("transform", `translate(${width / 2}, 0)`) // Move to the middle
            .call(d3.axisLeft(y).tickSize(0))
            .selectAll("text")
            .attr("text-anchor", "middle")
            .on("mouseover", function (event, d) {
                const counts = careerCountData.find(data => data.sportcn === d);
                const { countLeft, countRight } = seriesCount(counts);

                svg.selectAll(".row-highlight")
                .filter((row) => row.sportcn === d)
                .attr('opacity', 0.8);

                setTooltipContent (`
                    <div class="tooltip__content">
                    <span class="tooltip__title"><b>${d}</b></span>
                    <span class="tooltip__details">
                    总体制内 : 总体制外</br>
                    <b><span style="color:#ED6E79">${countLeft}</span> : <span style="color:#658DE5">${countRight}</span></b>
                    </span>
                    </div>
                    `);
                // <span style="text-align:center; display:block"><span class="tooltip__details">
                //     <b>${d}</b></br>
                //     总体制内 : 总体制外 </br><b>
                //     <span style="color:#ED6E79">${countLeft}</span> : <span style="color:#658DE5">${countRight}</span></span></b></span>
                setTooltipPosition({ x: event.pageX + 10, y: event.pageY + 10 });
            })
            .on("mouseout", () => {
                svg.selectAll(".row-highlight")
                .attr("opacity", 0);
                setTooltipContent(null);
                setHovered(null);
            })
            ;

        // Hide y line
        svgGroup.selectAll("path, line")
            .attr("display", "none");

        svgGroup.append("g")
            .attr("class", "grid-lines")
            .selectAll("line")
            .data(xRight.ticks(5))
            .enter().append("line")
            .attr("x1", d => xRight(d))
            .attr("x2", d => xRight(d))
            .attr("y1", 0)
            .attr("y2", height)
            .lower();

        svgGroup.append("g")
            .attr("class", "grid-lines")
            .selectAll("line")
            .data(xLeft.ticks(5))
            .enter().append("line")
            .attr("x1", d => xLeft(d))
            .attr("x2", d => xLeft(d))
            .attr("y1", 0)
            .attr("y2", height);

        // x-axis careers right
        svgGroup.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0,${height})`)
            .call(xAxisRight)
            .call(g => g.select(".domain").remove());

        // x-axis careers left
        svgGroup.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0, ${height})`)
            .call(xAxisLeft)
            .call(g => g.select(".domain").remove());

        //chart-titles
        svgGroup.selectAll('.chart-title').remove(); 

        svgGroup.append('text')
            .attr('class', 'chart-title')
            .attr('x', xLeft(0) - 20)
            .attr('y', -25)
            .attr('text-anchor', 'middle')
            .text('体制内');

        svgGroup.append('text')
            .attr('class', 'chart-title')
            .attr('x', xRight(0) + 20)
            .attr('y', -25)
            .attr('text-anchor', 'middle')
            .text('体制外');
      

        return () => {
            svgGroup.selectAll('*').remove();
        }
    }

    const handleLegendMouseOver = (e, type) => {
        if (legendTooltip[type]) {
            setTooltipContent(legendTooltip[type]);
            setTooltipPosition({ x: e.pageX, y: e.pageY });
            setHovered(type);
        }
    };

    const handleLegendMouseOut = () => {
        setTooltipContent(null);
        setHovered(null);
    };

    return (
        <React.Fragment>
            <div className='content content__text'>
                <h2>一. 奥运冠军退役后多数留在体制内</h2>
                </div>
            <div ref={containerRef} className='chart-container' >
                
                <svg ref={svgRef} id='career-type' />
                <Tooltip content={tooltipContent} position={tooltipPosition} hovered={hovered} />
            </div>
            
            <div className='content'>
            <div className='legend' style={{ justifyContent: 'center', paddingBottom:'20px' }}>
                {careerTypes.map((type, i) => (
                    <div key={type} className='legend__item'
                    onMouseOver={(e) => handleLegendMouseOver(e, type)}
                    onMouseOut={handleLegendMouseOut}
                    >
                        <div className="legend__color" style={{ backgroundColor: color(type) }}></div>
                        <div className="legend__label">
                            {type}
                            {legendTooltip[type] && (
                            <span className="legend__asterisk">*</span>
                        )}
                            </div>
                    </div>
                ))}
                </div>

                <div className='scroll__caption chart-caption'>图注：将鼠标悬浮图表，可查看不同项目退役冠军具体就业情况。</div>

                {/* <div className='content'> */}
                    <div className='content__text'>
                    <p>相比西方常见的<a href='https://www.sport.gov.cn/n322/n3407/n3411/c564455/content.html' target='_blank'>社团管理型</a>模式，中国效法苏联，采取“<a href='http://www.chinatyxk.com/editer/doc/20068416395517735.pdf' target='_blank'>举国体制</a>”，直接由国家体育管理机构选拔, 培养运动员。从计划经济年代起，体育从中国教育中剥离出来，专业体育又从体育中分化出来，成为专门的“工种”。退役运动员，也享受“包分配”政策。奥运冠军退役后，除了做“教练”，他们大多进入体制内的“铁饭碗”，绝大多数在体育系统内任职，如体育局副主任等。<a href='https://francis-press.com/uploads/papers/HWfKyt4dWLu7i14FFHcRMKT0YkAFgPwAKVscAQMo.pdf' target='_blank' style={{ whiteSpace:'nowrap' }}>80年代起</a>，体育系统分配相对饱和，退役运动员也开始往非体育系统迁移。但从数据来看，跨出体育系统以外从政，这一比例在奥运冠军中所占比例极少。他们的仕途轨迹大多起步于体育系统内部，一路攀升，包括担任妇联副主席，共青团省委书记等官职。</p>

                    <p>2000年左右，在中国高校扩招背景下，中央和地方出台的一系列政策，允许退役运动员免试进入<a href='http://xb.hepec.edu.cn/ch/reader/create_pdf.aspx?file_no=20220406&flag=1&year_id=2022&quarter_id=4' target='_blank'>高等院校</a>，这丰富了奥运冠军们“第二人生”的可能性，越来越多的人毕业后去体育类或普通大学院校任职，大多谋得“副教授”甚至“院长”的职称。</p>

                    </div>
                {/* </div> */}
                {/* <Image src='/images/AP920803044.jpg' caption='1992年8月3日，中国乒乓球队员邓亚萍（右）和队友乔红在巴塞罗那夺得金牌后握手。（AP / Craig Fujii）' /> */}

                <ImageScroll
                    src1='/images/AP920803044.jpg'
                    caption1='1992年8月3日，中国乒乓球队员邓亚萍（右）和队友乔红在巴塞罗那夺得金牌后握手。（AP / Craig Fujii）'
                    src2='/images/000_Hkg1106910.jpg'
                    caption2='退役后的邓亚萍出国深造，曾任共青团北京市委副书记、人民日报社副秘书长。后经商，成立“邓亚萍体育产业投资基金”等。2008年,邓亚萍等人在“两会”间隙举行新闻发布会。(AFP / Frederic J. Brown)' 
                    />

                {/* <Image src='/images/000_Hkg1106910.jpg' caption='2008年3月12日，邓亚萍（左）与北京市副市长刘敬民（左二）、和中国体育部副部长崔大林（右）以及中电影导演张艺谋（右二）在北京人民大会堂“两会”间隙举行新闻发布会。(AFP / Frederic J. BROWN)' /> */}
            </div>

        </React.Fragment>
    );

};

export default CareerType;
